import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Icon, Text, Spinner } from "@ttc3k/trekker";
import { Center, Flex } from "styled-system/jsx";
import { useTranslation } from "react-i18next";
import { Plus } from "iconoir-react";
import { useOrdersForBroadcastResourceQuery } from "gql/generated";
import { Skeleton } from "shared/components/atoms/Skeleton";
import { OrderCard, OrderDetailModal } from "shared/components";
import { getMultilingualString, isEftPendingChargeId } from "shared/utils";
import { format } from "date-fns";
import { BookerModal } from "widgets/booker/features/BookerModal";
import { i18nLanguage } from "i18n";
import type { DefaultContact, UserOperator } from "widgets/booker/contexts/BookerContextProvider/BookerContext";
import { useMembershipCheckoutIntent } from "shared/contextProviders/membershipCheckoutIntent";

export type { DefaultContact };

type StorefrontOrdersIndexProps = {
  bookerId: string;
  broadcastId: string;
  resourceId: string;
  personId?: string;
  isPreview: boolean;
  language: i18nLanguage;
  defaultContact?: DefaultContact;
  userOperators?: UserOperator[];
  organizationId?: string;
  /** Admin-set label for the purchase row; replaces "Buy Now". */
  ctaLabel?: string;
};

export const StorefrontOrdersIndex = ({
  bookerId,
  broadcastId,
  resourceId,
  personId,
  isPreview,
  language,
  defaultContact,
  userOperators,
  organizationId,
  ctaLabel,
}: StorefrontOrdersIndexProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.BROADCAST",
  });

  const membershipCheckout = useMembershipCheckoutIntent(resourceId);
  const membershipIntent = membershipCheckout?.intent;
  // A membership intent can point at the tier's own checkout.
  const checkoutBookerId = membershipCheckout?.bookerId || bookerId;
  const [isBookerOpen, setIsBookerOpen] = useState(false);
  const didAutoOpen = useRef(false);
  useEffect(() => {
    if (didAutoOpen.current || isPreview || !checkoutBookerId) return;
    if (!membershipCheckout?.autoOpenBooker) return;
    if (membershipCheckout.resourceId !== resourceId) return;
    didAutoOpen.current = true;
    setIsBookerOpen(true);
    membershipCheckout.onBookerAutoOpened?.();
  }, [membershipCheckout, resourceId, isPreview, checkoutBookerId]);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const { data, loading, refetch } = useOrdersForBroadcastResourceQuery({
    variables: {
      broadcastResourceId: resourceId,
      personId: personId ?? "",
      bookerId: checkoutBookerId,
    },
    skip: isPreview || !personId,
  });

  const orders = useMemo(
    () => data?.ordersForBroadcastResource ?? [],
    [data],
  );

  const handleBookNow = () => {
    setIsBookerOpen(true);
  };

  const handleBookerClose = () => {
    setIsBookerOpen(false);
    refetch();
  };

  const handleOrderClick = useCallback((orderId: string) => {
    setSelectedOrderId(orderId);
  }, []);

  const handleCloseOrderDetail = useCallback(() => {
    setSelectedOrderId(null);
  }, []);

  if (loading) {
    return (
      <Center py="600">
        <Spinner w="38px" h="38px" />
      </Center>
    );
  }

  return (
    <>
    <OrderDetailModal
      orderId={selectedOrderId}
      open={!!selectedOrderId}
      onClose={handleCloseOrderDetail}
    />
    <Flex flexDir="column" gap="300">
      <Skeleton
        alignItems="stretch"
        minH="unset"
        cursor="pointer"
        onClick={handleBookNow}
      >
        <Flex alignItems="center" gap="200" py="100">
          <Icon Element={Plus} color="icon.mid" size="sm" />
          <Text visual="bodySemiBold" color="text.dark">
            {membershipIntent
              ? t(`VIEW_MODAL.MEMBERSHIP_CTA.${membershipIntent.kind}`, {
                  membership: membershipIntent.membershipNomenclature,
                  member: membershipIntent.memberNomenclature,
                })
              : ctaLabel || t("VIEW_MODAL.NEW_BOOKING")}
          </Text>
        </Flex>
      </Skeleton>

      {orders.length === 0 && (
        <Text
          visual="smallRegular"
          color="text.mid"
          textAlign="center"
          py="200"
        >
          {t("VIEW_MODAL.NO_BOOKINGS_YET")}
        </Text>
      )}

      {orders.map((order, idx) => {
        if (!order) return null;
        const productName = order.purchasedProducts?.[0]?.product?.name
          ? getMultilingualString(order.purchasedProducts[0].product.name)
          : null;
        const title =
          productName ||
          t("VIEW_MODAL.BOOKING_NUMBER", { number: orders.length - idx });
        const dateStr = order.createdDateISO
          ? format(new Date(order.createdDateISO), "MMM d, yyyy h:mm a")
          : "";
        const purchaserName = order.customer
          ? `${order.customer.firstName} ${order.customer.lastName}`
          : undefined;

        const isEftPending = isEftPendingChargeId(
          order.stripeMetadata?.chargeId,
        );

        return (
          <OrderCard
            key={order._id}
            orderId={order._id}
            title={title}
            purchaserName={purchaserName}
            dateStr={dateStr}
            isCancelled={order.cancelled}
            isPending={!order.cancelled && isEftPending}
            cancelledLabel={t("VIEW_MODAL.BOOKING_CANCELLED")}
            confirmedLabel={t("VIEW_MODAL.BOOKING_CONFIRMED")}
            pendingLabel={t("VIEW_MODAL.BOOKING_PENDING")}
            onClick={() => handleOrderClick(order._id)}
          />
        );
      })}

      {checkoutBookerId && (
        <BookerModal
          bookerId={checkoutBookerId}
          open={isBookerOpen}
          onClose={handleBookerClose}
          bookerPosition="right"
          language={language}
          broadcastId={broadcastId}
          broadcastResourceId={resourceId}
          defaultContact={defaultContact}
          userOperators={userOperators}
          organizationId={organizationId}
          membershipIntent={membershipIntent}
        />
      )}
    </Flex>
    </>
  );
};
